<template>
  <div
    v-if="isEnabled"
    class="custom-cursor-container"
    :class="{
      'cursor--hidden': !isVisible,
      'cursor--hover': isHovered,
      'cursor--click': isClicked,
    }"
  >
    <div ref="follower" class="custom-cursor-follower"></div>
    <div ref="dot" class="custom-cursor-dot"></div>
  </div>
</template>

<script>
export default {
  name: "CustomCursor",
  data() {
    return {
      isEnabled: false,
      isVisible: false,
      isHovered: false,
      isClicked: false,
      mouseX: -100,
      mouseY: -100,
      dotX: -100,
      dotY: -100,
      followerX: -100,
      followerY: -100,
      rafId: null,
    };
  },
  mounted() {
    if (typeof window === "undefined") return;

    // Отключаем на тач-устройствах для идеального нативного мобильного UX
    const isTouchOnly = window.matchMedia(
      "(hover: none) and (pointer: coarse)"
    ).matches;

    if (isTouchOnly) {
      this.isEnabled = false;
      return;
    }

    this.isEnabled = true;
    document.documentElement.classList.add("has-custom-cursor");

    // Защитный обработчик: при первом же тапе на гибридных экранах отключаем курсор
    this.touchHandler = () => {
      this.isEnabled = false;
      document.documentElement.classList.remove("has-custom-cursor");
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    };
    window.addEventListener("touchstart", this.touchHandler, {
      passive: true,
      once: true,
    });

    window.addEventListener("mousemove", this.onMouseMove, { passive: true });
    window.addEventListener("mousedown", this.onMouseDown, { passive: true });
    window.addEventListener("mouseup", this.onMouseUp, { passive: true });
    document.addEventListener("mouseleave", this.onMouseLeave);
    document.addEventListener("mouseenter", this.onMouseEnter);

    this.startRenderLoop();
  },
  beforeDestroy() {
    if (typeof window === "undefined") return;
    document.documentElement.classList.remove("has-custom-cursor");
    if (this.touchHandler) {
      window.removeEventListener("touchstart", this.touchHandler);
    }
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("mouseleave", this.onMouseLeave);
    document.removeEventListener("mouseenter", this.onMouseEnter);

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  },
  methods: {
    onMouseMove(e) {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;

      if (!this.isVisible) {
        this.isVisible = true;
        this.dotX = this.mouseX;
        this.dotY = this.mouseY;
        this.followerX = this.mouseX;
        this.followerY = this.mouseY;
      }

      // Проверка на интерактивные элементы под курсором
      const target = e.target;
      if (target) {
        const interactive = target.closest(
          'a, button, input, select, textarea, [role="button"], .client-card, .ecosystem-card, .bento-item, .pricing-card, .filter-btn, .zone-btn, .coupon-tab-btn, .office-tab-btn, .floating-badge, .contact-card'
        );
        this.isHovered = Boolean(interactive);
      }
    },
    onMouseDown() {
      this.isClicked = true;
    },
    onMouseUp() {
      this.isClicked = false;
    },
    onMouseLeave() {
      this.isVisible = false;
    },
    onMouseEnter() {
      this.isVisible = true;
    },
    startRenderLoop() {
      const render = () => {
        // Мягкая интерполяция (lerp) для шелковистой плавности
        const dotEase = 0.35;
        const followerEase = 0.16;

        this.dotX += (this.mouseX - this.dotX) * dotEase;
        this.dotY += (this.mouseY - this.dotY) * dotEase;

        this.followerX += (this.mouseX - this.followerX) * followerEase;
        this.followerY += (this.mouseY - this.followerY) * followerEase;

        const scale = this.isClicked ? 0.85 : 1;
        const dotScale = this.isClicked ? 1.4 : 1;

        if (this.$refs.dot) {
          this.$refs.dot.style.transform = `translate3d(${this.dotX}px, ${this.dotY}px, 0) translate(-50%, -50%) scale(${dotScale})`;
        }

        if (this.$refs.follower) {
          this.$refs.follower.style.transform = `translate3d(${this.followerX}px, ${this.followerY}px, 0) translate(-50%, -50%) scale(${scale})`;
        }

        this.rafId = requestAnimationFrame(render);
      };

      this.rafId = requestAnimationFrame(render);
    },
  },
};
</script>

<style lang="scss">
// Скрываем стандартный системный курсор только когда активен кастомный
html.has-custom-cursor {
  body,
  a,
  button,
  [role="button"],
  .v-btn,
  .client-card,
  .ecosystem-card,
  .bento-item,
  .pricing-card {
    cursor: none !important;
  }
}

.custom-cursor-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 999999;
  overflow: hidden;
  transition: opacity 0.3s ease;

  &.cursor--hidden {
    opacity: 0;
  }

  .custom-cursor-dot {
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 6px;
    background: #00e676;
    border-radius: 50%;
    pointer-events: none;
    box-shadow: 0 0 10px rgba(0, 230, 118, 0.9), 0 0 4px #00e676;
    will-change: transform;
    transition: width 0.2s ease, height 0.2s ease, opacity 0.2s ease;
  }

  .custom-cursor-follower {
    position: absolute;
    top: 0;
    left: 0;
    width: 34px;
    height: 34px;
    border: 1.5px solid rgba(0, 230, 118, 0.75);
    background: rgba(0, 230, 118, 0.04);
    border-radius: 50%;
    pointer-events: none;
    will-change: transform;
    transition: width 0.25s cubic-bezier(0.16, 1, 0.3, 1),
      height 0.25s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.25s ease,
      border-color 0.25s ease, box-shadow 0.25s ease;
    box-shadow: 0 0 16px rgba(0, 230, 118, 0.15);
  }

  // Состояние наведения на интерактивный элемент
  &.cursor--hover {
    .custom-cursor-dot {
      width: 4px;
      height: 4px;
      background: #ffffff;
      box-shadow: 0 0 8px #ffffff;
    }

    .custom-cursor-follower {
      width: 54px;
      height: 54px;
      background: rgba(0, 230, 118, 0.14);
      border-color: #00e676;
      box-shadow: 0 0 24px rgba(0, 230, 118, 0.35);
      backdrop-filter: blur(1px);
    }
  }

  // Состояние клика
  &.cursor--click {
    .custom-cursor-follower {
      background: rgba(0, 230, 118, 0.22);
      border-color: #00e676;
    }
  }
}
</style>
